import { SetStateAction, useState, type FormEvent } from "react";
import { SlideUpLine, FadeUp, Stagger, StaggerItem } from "@/pages/_shared/anim";
import ReCAPTCHA from "react-google-recaptcha";
import BrochurePdf from "@/assets/2026 UPlift catalog_Lite.pdf";

const inquiryTypes = [
  "Spare Part Enquiry",
  "Technical Support",
  "PCB Repair",
  "Supply & Installation",
  "Maintenance & Upgradation",
  "Testing & Commissioning",
];

const SITE_KEY = "6LcqH2EtAAAAAH4kWU7dB2gHeTrv41IKgLIzYP26";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
    "idle"
  );
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
     if (!captchaToken) {
      setError("Please complete the CAPTCHA.");
    return;
  }
    const form = e.currentTarget;
    const data = new FormData(form);
     data.append("captcha", captchaToken);

    const message = (data.get("message") as string) || "";
    if (message.length > 500) {
      setError("Please keep your message under 500 characters.");
      return;
    }

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

   try {
      setStatus("loading");
      const res = await fetch("/api/send-mail.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!res.ok) throw new Error("submit failed");
      setStatus("success");
      form.reset();
      setCaptchaToken(null);
    } catch {
      setStatus("error");
      setError("Something went wrong. Please try again or call us directly.");
    }
  };

  return (
    <section id="contact" className="bg-crest-deep py-20 md:py-28 relative overflow-hidden">
      <div className="px-6 md:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-crest-orange uppercase">
                <span className="w-6 h-px bg-crest-orange/60" /> Start the conversation
              </span>
            </FadeUp>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-tight mt-5">
              <SlideUpLine as="div">Not Sure What Your</SlideUpLine>
              <SlideUpLine as="div" delay={0.08}>Elevator Needs?</SlideUpLine>
            </h2>
            <FadeUp delay={0.25}>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mt-6 max-w-md">
                Send us the elevator brand, system details, part number, technical issue, or component photo. Our team can review your requirement and help identify the right solution.
              </p>
            </FadeUp>

            <Stagger className="mt-10 space-y-5" stagger={0.08}>
              <StaggerItem className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-crest-orange text-white shrink-0">
                  <i className="ri-phone-line text-lg"></i>
                </span>
                <div>
                  <div className="text-white font-semibold">Technical Support</div>
                  <a
                    href="tel:0505664336"
                    className="text-white/65 hover:text-crest-orange text-sm cursor-pointer"
                  >
                    050 566 43 36
                  </a>
                </div>
              </StaggerItem>
              <StaggerItem className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-crest-orange text-white shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <div className="text-white font-semibold">Spare Part Enquiry</div>
                  <a
                    href="mailto:upliftone1@gmail.com"
                    className="text-white/65 hover:text-crest-orange text-sm cursor-pointer"
                  >
                    upliftone1@gmail.com
                  </a>
                </div>
              </StaggerItem>
              <StaggerItem className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-crest-orange text-white shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </span>
                <div>
                  <div className="text-white font-semibold">Location</div>
                  <div className="text-white/65 text-sm leading-relaxed">
                    Abu Dhabi – UAE
                  </div>
                </div>
              </StaggerItem>
            </Stagger>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-7">
            <form
              id="uplift-home-contact"
              data-readdy-form
              onSubmit={onSubmit}
              className="bg-white rounded-lg p-6 md:p-10 border border-white/10"
            >
              {status === "success" ? (
                <div className="flex flex-col items-center text-center py-10">
                  <span className="w-16 h-16 flex items-center justify-center rounded-full bg-crest-orange text-white mb-5">
                    <i className="ri-check-line text-3xl"></i>
                  </span>
                  <h3 className="font-display text-3xl text-crest-deep">
                    Thanks — your enquiry has been received.
                  </h3>
                  <p className="text-crest-deep/70 mt-3 max-w-md">
                    A member of our technical team will review your requirement and respond within one business day.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 inline-flex items-center gap-2 bg-crest-orange text-white px-6 py-3 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                        Full name *
                      </label>
                      <input
                        required
                        name="name"
                        type="text"
                        placeholder="Your full name"
                        className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                        Email *
                      </label>
                      <input
                        required
                        name="email"
                        type="email"
                        placeholder="you@company.com"
                        className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                        Phone / WhatsApp
                      </label>
                      <input
                        name="phone"
                        type="tel"
                        placeholder="+971 50 123 4567"
                        className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                        Elevator / Escalator Brand
                      </label>
                      <input
                        name="brand"
                        type="text"
                        placeholder="Brand name"
                        className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                        Inquiry type *
                      </label>
                      <select
                        required
                        name="inquiry_type"
                        defaultValue=""
                        className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none appearance-none cursor-pointer"
                      >
                        <option value="" disabled>
                          Select inquiry type…
                        </option>
                        {inquiryTypes.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                        Part Number (optional)
                      </label>
                      <input
                        name="part_number"
                        type="text"
                        placeholder="Part number if known"
                        className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-xs font-semibold text-crest-deep/70 uppercase tracking-wider">
                      Requirement details *
                    </label>
                    <textarea
                      required
                      name="message"
                      maxLength={500}
                      rows={5}
                      placeholder="Describe your elevator requirement, technical issue, or spare part need…"
                      className="mt-2 w-full bg-crest-cream border border-transparent focus:border-crest-deep rounded-md px-4 py-3 text-sm text-crest-deep outline-none placeholder:text-crest-deep/40 resize-none"
                    />
                    <div className="text-xs text-crest-deep/50 mt-1">
                      Max 500 characters.
                    </div>
                  </div>

                  {error && (
                    <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                      {error}
                    </div>
                  )}

                    <div className="mt-6">
                      <ReCAPTCHA
                          sitekey={SITE_KEY}
                          onChange={(token: SetStateAction<string>) => {
                          setCaptchaToken(token);
                          if (error === "Please complete the CAPTCHA.") {
                            setError("");
                           }
                             }}
                              onExpired={() => {
                                setCaptchaToken(null);
                           }}
                           onErrored={() => {
                              setCaptchaToken(null);
                          }}
                        />
                  </div>                

               <div className="mt-6 flex flex-wrap gap-4">
                  <button
                    type="submit"
                    disabled={status === "loading" || !captchaToken}
                    className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-crest-orange text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-crest-orangeLight transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                  >
                    {status === "loading" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending…
                      </>
                    ) : (
                      <>
                        SUBMIT REQUIREMENT
                        <i className="ri-arrow-right-up-line"></i>
                      </>
                    )}
                  </button>
                   <a
                      href={BrochurePdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-crest-orange text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-crest-orangeLight transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                    >
                   <i className="ri-file-pdf-line"></i>
                     VIEW Catelog
                  </a>
                </div>
                </>
              )}
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}